/**
 * Findings API Hooks
 *
 * Query and mutation hooks for the audit findings feature.
 * Wraps Convex finding_queries and finding_mutations with consistent
 * patterns for error handling, toasts, and logging.
 */

import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "@/packages/types";
import { useApiMutation } from "./use-api-mutation";

// -- Queries ----------------------------------------------------------------

export function useFindingsByAudit(auditId: Id<"audits">) {
  return useQuery(api.finding_queries.getByAudit, { auditId });
}

export function useFindingsByChecklistItem(
  checklistItemId: Id<"checklistItems">,
) {
  return useQuery(api.finding_queries.getByChecklistItem, {
    checklistItemId,
  });
}

export function useDraftFindingsByChecklistItem(
  checklistItemId: Id<"checklistItems">,
) {
  return useQuery(api.finding_queries.getDraftsByChecklistItem, {
    checklistItemId,
  });
}

export function useFindingById(id: Id<"findings"> | null) {
  return useQuery(api.finding_queries.getById, { id });
}

// -- Mutations --------------------------------------------------------------

export function useGenerateFindings() {
  return useApiMutation(api.finding_mutations.generateFindings, {
    name: "generate-findings",
    silentSuccess: true,
  });
}

export function useCreateManualFinding() {
  return useApiMutation(api.finding_mutations.createManual, {
    name: "create-manual-finding",
    successMessage: "Finding created",
  });
}

export function useConfirmFinding() {
  return useApiMutation(api.finding_mutations.confirmFinding, {
    name: "confirm-finding",
    silentSuccess: true,
  });
}

export function useConfirmAllFindings() {
  return useApiMutation(api.finding_mutations.confirmAll, {
    name: "confirm-all-findings",
    successMessage: "All findings confirmed",
  });
}

export function useDiscardFinding() {
  return useApiMutation(api.finding_mutations.discardFinding, {
    name: "discard-finding",
    silentSuccess: true,
  });
}

export function useDiscardAllDrafts() {
  return useApiMutation(api.finding_mutations.discardAllDrafts, {
    name: "discard-all-drafts",
    successMessage: "Draft findings discarded",
  });
}

export function useUpdateFinding() {
  return useApiMutation(api.finding_mutations.updateFinding, {
    name: "update-finding",
    silentSuccess: true,
  });
}

export function useResolveFinding() {
  return useApiMutation(api.finding_mutations.resolveFinding, {
    name: "resolve-finding",
    successMessage: "Finding resolved",
  });
}

export function useReopenFinding() {
  return useApiMutation(api.finding_mutations.reopenFinding, {
    name: "reopen-finding",
    successMessage: "Finding reopened",
  });
}

export function useDeleteFinding() {
  return useApiMutation(api.finding_mutations.deleteFinding, {
    name: "delete-finding",
    successMessage: "Finding deleted",
  });
}
